import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";

// Fallback slides in case DB is empty
import heroImage from "@/assets/hero-necklace-star.jpg";

interface HeroSlide {
  id: string;
  image_url: string | null;
  title: string | null;
  subtitle: string | null;
  button_text: string | null;
  link_type: string;
  link_value: string | null;
  display_order: number;
}

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const { data, error } = await supabase
          .from('hero_slides')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true });

        if (error) throw error;

        if (data && data.length > 0) {
          setSlides(data);
        } else {
          // Fallback to default slide if no slides in DB
          setSlides([{
            id: 'default',
            image_url: heroImage,
            title: 'מגוון רחב של תכשיטי זהב',
            subtitle: 'מייצב התרבות והמומחיות',
            button_text: 'גלה עוד',
            link_type: 'internal',
            link_value: '/shop',
            display_order: 0
          }]);
        }
      } catch (error) {
        console.error('Error fetching slides:', error);
        // Use fallback on error
        setSlides([{
          id: 'default',
          image_url: heroImage,
          title: 'מגוון רחב של תכשיטי זהב',
          subtitle: 'מייצב התרבות והמומחיות',
          button_text: 'גלה עוד',
          link_type: 'internal',
          link_value: '/shop',
          display_order: 0
        }]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSlides();
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const getSlideLink = (slide: HeroSlide) => {
    if (!slide.link_value) return '/shop';
    
    switch (slide.link_type) {
      case 'category':
        return `/collections/${slide.link_value}`;
      case 'external':
        return slide.link_value;
      case 'internal':
      default:
        return slide.link_value;
    }
  };

  if (isLoading) {
    return (
      <section className="relative w-full h-[400px] md:h-[500px] flex items-center justify-center overflow-hidden px-4 lg:px-8 bg-muted">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </section>
    );
  }

  if (slides.length === 0) return null;

  const currentSlideData = slides[currentSlide];
  const isExternal = currentSlideData?.link_type === 'external';

  return (
    <section className="relative w-full h-[400px] md:h-[500px] flex items-center justify-center overflow-hidden px-4 lg:px-8" aria-label="סליידר ראשי" aria-roledescription="carousel">
      {/* Background Images with crossfade - both images visible during transition */}
      <AnimatePresence initial={false}>
        <motion.div
          key={currentSlide}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
        >
          <motion.img
            src={currentSlideData?.image_url || heroImage}
            alt={currentSlideData?.title || "אליס תכשיטים - קולקציית תכשיטי זהב ויהלומים יוקרתיים"}
            className="absolute inset-0 w-full h-full object-cover"
            width={1920}
            height={1080}
            fetchPriority={currentSlide === 0 ? "high" : "auto"}
            loading={currentSlide === 0 ? "eager" : "lazy"}
            decoding="async"
            initial={{ scale: 1 }}
            animate={{ scale: 1.08 }}
            transition={{ duration: 5, ease: "linear" }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/30" />

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 lg:left-12 top-1/2 -translate-y-1/2 z-20 text-white/90 hover:text-white transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="h-10 w-10 lg:h-12 lg:w-12 no-flip" strokeWidth={1} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 lg:right-12 top-1/2 -translate-y-1/2 z-20 text-white/90 hover:text-white transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="h-10 w-10 lg:h-12 lg:w-12 no-flip" strokeWidth={1} />
          </button>
        </>
      )}

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-3xl mx-auto text-white">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            {currentSlideData?.title && (
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-3 leading-tight">
                {currentSlideData.title}
              </h2>
            )}
            {currentSlideData?.subtitle && (
              <p className="text-sm md:text-base lg:text-lg mb-4 md:mb-8 font-light">
                {currentSlideData.subtitle}
              </p>
            )}
            {currentSlideData?.button_text && (
              <Button
                asChild
                variant="default"
                size="lg"
                className="bg-white/10 text-white border border-white/30 hover:bg-white hover:text-primary backdrop-blur-sm transition-all duration-300"
              >
                {isExternal ? (
                  <a href={getSlideLink(currentSlideData)} target="_blank" rel="noopener noreferrer">
                    {currentSlideData.button_text}
                  </a>
                ) : (
                  <Link to={getSlideLink(currentSlideData)}>
                    {currentSlideData.button_text}
                  </Link>
                )}
              </Button>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Dots Navigation */}
      {slides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-1">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className="flex items-center justify-center w-6 h-6"
              aria-label={`Go to slide ${index + 1}`}
            >
              <span className={`block rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-white w-8 h-2'
                  : 'bg-white/50 hover:bg-white/75 w-2 h-2'
              }`} />
            </button>
          ))}
        </div>
      )}
    </section>
  );
};

export default Hero;
